import React, { useEffect, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { ORG_ID, HOST } from 'config';
import { buildUrl } from 'utils/url';

export default function OAuthCallbackHandler() {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const showNotification = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code && state) {
        try {
          // Use the stored user token instead of admin token
          const userToken = localStorage.getItem('googleAuthUserToken');
          if (!userToken) {
            console.error('No user token found for OAuth callback');
            showNotification('Authorization session expired. Please try again.', 'error');
            return;
          }

          const response = await fetch(buildUrl(HOST, ORG_ID ? `/org/${ORG_ID}` : '', '/auth/oauth2/callback'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${userToken}`,
            },
            body: JSON.stringify({
              code,
              state,
            }),
          });

          if (response.ok) {
            const result = await response.json();
            console.log('OAuth callback successful:', result);

            // Clean up stored user token
            localStorage.removeItem('googleAuthUserToken');

            // Get the original path from localStorage if it was stored
            const originalPath = localStorage.getItem('googleAuthOriginalPath');

            // Clean up URL parameters
            const url = new URL(window.location);
            url.searchParams.delete('code');
            url.searchParams.delete('state');

            // Show success notification
            showNotification('Google authorization successful!', 'success');

            // Navigate back to the original React Admin path or default
            if (originalPath) {
              localStorage.removeItem('googleAuthOriginalPath');
              // Use setTimeout to allow notification to show before redirect
              setTimeout(() => {
                window.location.href = window.location.origin + originalPath;
              }, 1500);
            } else {
              window.history.replaceState({}, document.title, url.pathname + url.search);
            }
          } else {
            console.error('OAuth callback failed:', response.statusText);
            showNotification('Authorization failed. Please try again.', 'error');
          }
        } catch (error) {
          console.error('Error handling OAuth callback:', error);
          showNotification('An error occurred during authorization.', 'error');
        }
      }
    };

    handleOAuthCallback();
  }, []);

  // This component doesn't render anything visible
  return (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );
}
